import { supabase } from '../../utils/supabase';
import { statsCache } from '../../utils/statsCache';

const RESET_PASSWORD = 'Wax123456';

export interface ResetState {
  selectedDojos: string[];
  selectedSeasons: string[];
  passwordInput: string;
  passwordAttempts: number;
  lockedUntil: number | null;
}

export class ResetService {
  validatePassword(password: string): boolean {
    return password === RESET_PASSWORD;
  }

  checkLock(state: ResetState): { isLocked: boolean; remainingMinutes?: number } {
    if (state.lockedUntil && Date.now() < state.lockedUntil) {
      const remaining = Math.ceil((state.lockedUntil - Date.now()) / 60000);
      return { isLocked: true, remainingMinutes: remaining };
    }
    return { isLocked: false };
  }

  incrementAttempt(state: ResetState): ResetState {
    const newAttempts = state.passwordAttempts + 1;
    if (newAttempts >= 3) {
      return {
        ...state,
        passwordAttempts: newAttempts,
        lockedUntil: Date.now() + 5 * 60 * 1000,
      };
    }
    return { ...state, passwordAttempts: newAttempts };
  }

  resetLock(): ResetState {
    return {
      selectedDojos: [],
      selectedSeasons: [],
      passwordInput: '',
      passwordAttempts: 0,
      lockedUntil: null,
    };
  }

  async calculatePreviewCount(selectedDojos: string[], selectedSeasons: string[]): Promise<number> {
    try {
      let query = supabase.from('game_records').select('*', { count: 'exact' });

      if (selectedDojos.length > 0 && !selectedDojos.includes('all')) {
        query = query.in('dojo_id', selectedDojos.map(id => parseInt(id)));
      }
      if (selectedSeasons.length > 0 && !selectedSeasons.includes('all')) {
        query = query.in('season_id', selectedSeasons.map(id => parseInt(id)));
      }

      const { count } = await query;
      return count || 0;
    } catch (err) {
      console.error('[V100] 计算预览数量失败:', err);
      return 0;
    }
  }

  async executeReset(selectedDojos: string[], selectedSeasons: string[]): Promise<number> {
    try {
      let gameIdsQuery = supabase.from('game_records').select('id');

      if (selectedDojos.length > 0 && !selectedDojos.includes('all')) {
        gameIdsQuery = gameIdsQuery.in('dojo_id', selectedDojos.map(id => parseInt(id)));
      }
      if (selectedSeasons.length > 0 && !selectedSeasons.includes('all')) {
        gameIdsQuery = gameIdsQuery.in('season_id', selectedSeasons.map(id => parseInt(id)));
      }

      const { data: gamesToDelete } = await gameIdsQuery;

      if (gamesToDelete && gamesToDelete.length > 0) {
        const gameIds = gamesToDelete.map(g => g.id);
        await supabase.from('player_round_records').delete().in('game_record_id', gameIds);
        await supabase.from('game_records').delete().in('id', gameIds);
        
        // V620: 清除缓存，确保重置后数据立即更新
        console.log('[V620] 数据重置完成，开始清除缓存...');
        
        // 清除全局缓存
        statsCache.remove('player_stats:all:all');
        statsCache.remove('penalty_records:all:all');
        
        // 清除所有可能的道场/赛季组合缓存
        for (let dojoId = 1; dojoId <= 10; dojoId++) {
          for (let seasonId = 1; seasonId <= 10; seasonId++) {
            statsCache.remove(`player_stats:${dojoId}:${seasonId}`);
            statsCache.remove(`penalty_records:${dojoId}:${seasonId}`);
          }
        }
        
        console.log('[V620] 已清除所有玩家统计和扣分记录缓存');
        
        return gameIds.length;
      }

      return 0;
    } catch (err) {
      console.error('[V100] 执行数据重置失败:', err);
      throw err;
    }
  }

  toggleDojoSelection(currentDojos: string[], dojoId: string): string[] {
    if (dojoId === 'all') {
      return ['all'];
    }
    const filtered = currentDojos.filter(id => id !== 'all');
    if (filtered.includes(dojoId)) {
      return filtered.filter(id => id !== dojoId);
    }
    return [...filtered, dojoId];
  }

  toggleSeasonSelection(currentSeasons: string[], seasonId: string): string[] {
    if (seasonId === 'all') {
      return ['all'];
    }
    const filtered = currentSeasons.filter(id => id !== 'all');
    if (filtered.includes(seasonId)) {
      return filtered.filter(id => id !== seasonId);
    }
    return [...filtered, seasonId];
  }
}

export const resetService = new ResetService();
